import React from 'react';
import dayjs from 'dayjs';

import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { ReportOutput, VarName } from '../../services/api';
import SummaryStartReport from './SummaryStartReport';
import SummaryEndReport from './SummaryEndReport';
import CO2Report from './CO2Report';
import TempReport from './TempReport';
import HumidityReport from './HumidityReport';
import PmReport from './PmReport';
import UtlReport from './UtlReport';
import useStyles from '../../styles/report';
import { getCurrentLocation, getLocationsById, getThemeMode } from '../../state/selectors';
import whiteLogo from '../../logo_512x512_white.png';
import greenLogo from '../../logo_512x512_teal.png';
import { ThemeMode } from '../../state/types';
import logoBuildingControl from '../../logoBuildingControl.png';

interface ReportContentProps {
  reportData: ReportOutput;
  lastMonthData: ReportOutput;
  closeReport: () => void;
}

function ReportContent({
  reportData,
  lastMonthData,
  closeReport,
}: ReportContentProps): JSX.Element {
  const classes = useStyles();
  const themeMode = useSelector(getThemeMode);
  const currentLocation = useSelector(getCurrentLocation);
  const locationsById = useSelector(getLocationsById);
  const { bands, utl, period } = reportData;

  // co2 data
  const co2Data = bands.find((data) => data.varName === VarName.Co2ppm);
  const lastMonthCo2DesiredPct = lastMonthData.bands.find((data) => data.varName === VarName.Co2ppm)
    ?.summary.desiredPct;

  // temperature data
  const tempData = bands.find((data) => data.varName === VarName.TemperatureC);
  const lastMonthTempDesiredPct = lastMonthData.bands.find(
    (data) => data.varName === VarName.TemperatureC
  )?.summary.desiredPct;

  // humidity data
  const humData = bands.find((data) => data.varName === VarName.RelativeHumidity);
  const lastMonthHumDesiredPct = lastMonthData.bands.find(
    (data) => data.varName === VarName.RelativeHumidity
  )?.summary.desiredPct;

  // particulate matter data
  const pmData = bands.find((data) => data.varName === VarName.ParticulateMatter);
  const lastMonthPmDesiredPct = lastMonthData.bands.find(
    (data) => data.varName === VarName.ParticulateMatter
  )?.summary.desiredPct;

  // utilisation data
  const utlData = utl.find((data) => data.varName === VarName.ClientsWiFi);
  const lastMonthUtlData = lastMonthData.utl.find((data) => data.varName === VarName.ClientsWiFi);

  return (
    <div className={`${classes.reportContent} print`}>
      <AppBar className={`${classes.header} no-print`}>
        <Toolbar>
          <img
            src={themeMode === ThemeMode.dark ? whiteLogo : greenLogo}
            alt="logo"
            height="35"
            width="35"
          />
          <div style={{ flex: '1 1 auto' }} />
          <Button
            onClick={() => window.print()}
            className={classes.headerButton}
            startIcon={<PrintIcon />}
          >
            Print
          </Button>
          <Button onClick={closeReport} className={classes.headerButton} startIcon={<CloseIcon />}>
            Close
          </Button>
        </Toolbar>
      </AppBar>
      <main className={`${classes.main} ${classes.reportContainer}`}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 30px',
          }}
        >
          <Typography className={classes.headerText}>
            {dayjs(period.start).format('MMMM YYYY')} for <br />{' '}
            {locationsById.get(currentLocation)?.name ?? currentLocation}
          </Typography>
          <div className={classes.reportLogo}>
            <img src={logoBuildingControl} alt="logo" style={{ width: '120px' }} />
          </div>
        </div>
        {/* <ReportHeader reportData={reportData} /> */}
        <SummaryStartReport reportData={reportData} lastMonthData={lastMonthData} />
        {co2Data && <CO2Report co2Data={co2Data} lastMonthDesiredPct={lastMonthCo2DesiredPct} />}
        {tempData && (
          <TempReport tempData={tempData} lastMonthDesiredPct={lastMonthTempDesiredPct} />
        )}
        {humData && (
          <HumidityReport humData={humData} lastMonthDesiredPct={lastMonthHumDesiredPct} />
        )}
        {pmData && <PmReport pmData={pmData} lastMonthDesiredPct={lastMonthPmDesiredPct} />}
        {utlData && <UtlReport utlData={utlData} lastMonthUtlData={lastMonthUtlData} />}
        <SummaryEndReport reportData={reportData} />
        {/* <ReportFooter /> */}
      </main>
    </div>
  );
}

export default ReportContent;
